import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const STATUS_STYLE = {
  open: "border-[#D4AF37]/60 text-[#D4AF37]",
  joinee_selected: "border-white/30 text-white",
  awaiting_deposits: "border-[#FFB300]/60 text-[#FFB300]",
  confirmed: "border-[#4CAF50]/60 text-[#4CAF50]",
  completed: "border-[#4CAF50]/60 text-[#4CAF50]",
  cancelled: "border-[#E53935]/60 text-[#E53935]",
};

export const PlansTab = () => {
  const [rows, setRows] = useState(null);

  const load = useCallback(() => {
    api
      .get("/admin/plans")
      .then((r) => setRows(r.data))
      .catch(() => {
        setRows([]);
        toast.error("Could not load plans");
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (planId) => {
    try {
      await api.delete(`/admin/plans/${planId}`);
      toast.success("Plan deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete plan");
    }
  };

  if (rows === null) return <Skeleton className="h-40 w-full rounded-xl bg-[#15151A]" />;

  return (
    <div className="bg-[#15151A] border border-[#D4AF37]/20 rounded-xl overflow-x-auto" data-testid="admin-plans-tab">
      <Table>
        <TableHeader>
          <TableRow className="border-white/10 hover:bg-transparent">
            <TableHead className="text-[#A0A0A0] uppercase text-xs tracking-widest">Plan</TableHead>
            <TableHead className="text-[#A0A0A0] uppercase text-xs tracking-widest">Planner</TableHead>
            <TableHead className="text-[#A0A0A0] uppercase text-xs tracking-widest">Joinee</TableHead>
            <TableHead className="text-[#A0A0A0] uppercase text-xs tracking-widest">Bill</TableHead>
            <TableHead className="text-[#A0A0A0] uppercase text-xs tracking-widest">Status</TableHead>
            <TableHead className="text-[#A0A0A0] uppercase text-xs tracking-widest text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((p) => (
            <TableRow key={p.plan_id} className="border-white/5 hover:bg-white/5" data-testid="admin-plan-row">
              <TableCell>
                <p className="text-sm text-white">{p.title}</p>
                <p className="text-xs text-[#A0A0A0]">
                  {p.neighborhood} · {format(new Date(p.datetime), "d MMM, h:mm a")}
                </p>
              </TableCell>
              <TableCell className="text-sm text-[#E0E0E0]">{p.planner_name}</TableCell>
              <TableCell className="text-sm text-[#E0E0E0]">{p.joinee_name || "—"}</TableCell>
              <TableCell className="text-sm text-[#D4AF37]">{inr(p.estimated_bill)}</TableCell>
              <TableCell>
                <Badge className={`bg-transparent border text-[10px] uppercase ${STATUS_STYLE[p.status] || "border-white/30 text-white"}`}>
                  {p.status.replace("_", " ")}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="admin-delete-plan-button"
                      className="border-[#E53935]/60 text-[#E53935] hover:bg-[#E53935]/10 hover:text-[#E53935] bg-transparent transition-colors"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#15151A] border-[#D4AF37]/30 text-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-serif">Delete this plan?</AlertDialogTitle>
                      <AlertDialogDescription className="text-[#A0A0A0]">
                        "{p.title}" and all its join requests and deposit records will be permanently removed.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel
                        data-testid="admin-delete-cancel"
                        className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white"
                      >
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => remove(p.plan_id)}
                        data-testid="admin-delete-confirm"
                        className="bg-[#E53935] text-white hover:bg-[#c62828]"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
